const n = {trigger: '[data-accordion-trigger]', panel: '[data-accordion-panel]'};
class r extends HTMLElement {
  constructor() {
    super(), this.addEventListener('click', this.handleClick.bind(this));
  }
  connectedCallback() {
    this.disconnectedCallback();
    this.elements = {triggers: this.querySelectorAll(n.trigger), panels: this.querySelectorAll(n.panel)};
    this.panelHeights = new Map();
    this.observePanelHeight();
    this.initPanels();
  }
  disconnectedCallback() {
    this.heightObserver?.disconnect(),
      this.bodyObserver?.disconnect(),
      this.panelResizeHandler && window.removeEventListener('resize', this.panelResizeHandler),
      (this.panelResizeHandler = null);
    window.cancelAnimationFrame(this.heightFrame);
    this.heightFrame = null;
    this.heightObserver = null;
    this.bodyObserver = null;
  }
  isPanelOpen(panel) {
    const trigger = Array.from(this.elements.triggers).find(trigger => trigger.getAttribute('aria-controls') === panel.id);
    return trigger ? trigger.getAttribute('aria-expanded') === 'true' : panel.classList.contains('show-panel');
  }
  initPanels() {
    const openPanels = Array.from(this.elements.panels).filter(panel => this.isPanelOpen(panel));
    if (this.observesBodies) {
      // Keep server-rendered content naturally sized until its first observation.
      openPanels.forEach(panel => { panel.style.height = 'auto'; });
      return;
    }
    const heights = openPanels.map(panel => [panel, panel.scrollHeight]);
    heights.forEach(([panel, height]) => { panel.style.height = `${height}px`; });
  }
  schedulePanelHeights() {
    if (this.heightFrame != null) return;
    this.heightFrame = window.requestAnimationFrame(() => {
      this.heightFrame = null;
      if (!this.isConnected) return;
      const openPanels = Array.from(this.elements.panels).filter(panel => this.isPanelOpen(panel));
      if (!this.observesBodies) {
        // Compatibility fallback: batch writes, then reads, rather than forcing
        // a layout for every panel or every individual mutation.
        openPanels.forEach(panel => { panel.style.height = 'auto'; });
        openPanels.forEach(panel => this.panelHeights.set(panel, panel.scrollHeight));
      }
      openPanels.forEach(panel => {
        const height = this.panelHeights.get(panel);
        if (height == null || panel.style.height === `${height}px`) return;
        panel.style.height = `${height}px`;
      });
    });
  }
  observePanelHeight() {
    const bodies = new Map(Array.from(this.elements.panels, panel => [panel.querySelector('.component-accordion__body'), panel]));
    this.observesBodies = typeof window.ResizeObserver === 'function' && !bodies.has(null);
    if (this.observesBodies) {
      const observer = this.heightObserver = new window.ResizeObserver(entries => {
        if (!this.isConnected || this.heightObserver !== observer) return;
        let changed = false;
        for (const entry of entries) {
          const panel = bodies.get(entry.target);
          if (!panel) continue;
          const box = entry.borderBoxSize?.[0] || entry.borderBoxSize;
          // Border-box observations include body padding and follow image/font
          // loads without resetting panel styles or reading synchronous layout.
          const height = Math.ceil(box?.blockSize ?? entry.target.offsetHeight);
          if (this.panelHeights.get(panel) === height) continue;
          this.panelHeights.set(panel, height);
          changed = true;
        }
        if (changed) this.schedulePanelHeights();
      });
      bodies.forEach((panel, body) => observer.observe(body, {box: 'border-box'}));
      const bodyObserver = this.bodyObserver = new MutationObserver(records => {
        if (!this.isConnected || this.bodyObserver !== bodyObserver) return;
        if (records.every(({target}) => bodies.get(target.querySelector('.component-accordion__body')) === target)) return;
        // A section may replace the wrapper itself, not just its contents.
        this.disconnectedCallback();
        this.panelHeights.clear();
        this.observePanelHeight();
        if (!this.observesBodies) this.schedulePanelHeights();
      });
      this.elements.panels.forEach(panel => bodyObserver.observe(panel, {childList: true}));
    } else {
      this.panelResizeHandler = () => this.schedulePanelHeights();
      window.addEventListener('resize', this.panelResizeHandler);
      this.heightObserver = new MutationObserver(this.panelResizeHandler);
      this.elements.panels.forEach(panel => this.heightObserver.observe(panel, {childList: true, subtree: true, characterData: true}));
    }
  }
  handleClick(e) {
    const t = e.target.closest(n.trigger);
    if (!t) return;
    const s = t.getAttribute('aria-controls'),
      i = this.querySelector(`#${s}`);
    t.hasAttribute('data-accordion-multi') || t.getAttribute('aria-multi') === '1' || this.closeAllPanels(t, i),
      this.togglePanel(t, i);
  }
  closeAllPanels(e, t) {
    this.elements.triggers.forEach((s) => {
      s !== e && s.setAttribute('aria-expanded', 'false');
    }),
      this.elements.panels.forEach((s) => {
        s !== t && (s.style.height = '0');
      });
  }
  togglePanel(e, t) {
    const s = e.getAttribute('aria-expanded') === 'true',
      i = s ? '0' : `${(this.observesBodies ? this.panelHeights.get(t) : null) ?? t.scrollHeight}px`;
    e.setAttribute('aria-expanded', (!s).toString()), (t.style.height = i);
  }
}
function o() {
  customElements.define('component-accordion', r);
}
export {o as i};
